import {
  type CombinedError,
  type Exchange,
  makeOperation,
  type Operation,
  type OperationResult,
} from "urql";
import {
  delay,
  filter,
  fromValue,
  makeSubject,
  merge,
  mergeMap,
  onPush,
  pipe,
  type Source,
  share,
} from "wonka";

/**
 * idempotent な read（GraphQL query）に対してのみ、transient な server-side error を
 * bounded backoff 付きで自動 retry する exchange。
 *
 * 背景: AI チャットの tool 実行ログでは "GraphQL 500 Internal Server Error" が最頻出の
 * tool error で、その多くは一過性（retry で成功）。backend は resolver error を
 * `consts.Error500` ("500 Internal Server Error") として GraphQL errors 配列で返すため、
 * urql 上では networkError ではなく graphQLErrors として現れる。
 *
 * mutation / subscription / teardown は retry しない（非冪等のため）。
 * 4xx（400/401/403/404 など client error）も retry しない。
 */

const DEFAULT_MAX_ATTEMPTS = 3; // 初回 + 2 retry
export const DEFAULT_INITIAL_DELAY_MS = 300;
const DEFAULT_MAX_DELAY_MS = 2000;

/**
 * transient とみなす error メッセージ。5xx と典型的な一過性ネットワーク/タイムアウト系のみ。
 * "400 Bad Request" / "404 Not Found" などは意図的に除外する。
 */
const RETRYABLE_MESSAGE_PATTERN =
  /\b(?:500|502|503|504)\b|internal server error|bad gateway|service unavailable|gateway time-?out|deadline exceeded|context deadline|temporarily unavailable|please try again|try again later|connection (?:reset|refused)|econnreset|etimedout|socket hang up/i;

const isRetryableMessage = (message: string | undefined): boolean =>
  typeof message === "string" && RETRYABLE_MESSAGE_PATTERN.test(message);

type ErrorResponse = { status?: number } | undefined;

/**
 * この CombinedError + operation が retry 対象かどうかを判定する純関数。
 * query 以外は常に false。
 */
export function isRetryableError(
  error: CombinedError | undefined,
  operation: Operation | undefined,
): boolean {
  if (!error || !operation) return false;
  // 非冪等な operation は retry しない。
  if (operation.kind !== "query") return false;

  // 真のネットワーク失敗（response なし）と 5xx response は retry。4xx は除外。
  if (error.networkError) {
    const response = (error as { response?: ErrorResponse }).response;
    const status = response?.status;
    if (status === undefined) return true; // fetch 自体が失敗（接続断など）
    if (status >= 500) return true;
    // status が 4xx の networkError は client error 扱いで retry しない
    if (status >= 400) return false;
  }

  // backend が 500 を GraphQL errors として返すケース（最頻出）。
  if (
    error.graphQLErrors?.some((graphQLError) =>
      isRetryableMessage(graphQLError?.message),
    )
  ) {
    return true;
  }

  // networkError があり status 不明（4xx でない）なら transient 扱い。
  if (error.networkError) {
    return isRetryableMessage(error.networkError.message);
  }

  return false;
}

/**
 * 指数バックオフ + フルジッター。attempt は 1 始まり（1 回目の retry = 1）。
 */
export function computeBackoffMs(
  attempt: number,
  initialDelayMs: number = DEFAULT_INITIAL_DELAY_MS,
  maxDelayMs: number = DEFAULT_MAX_DELAY_MS,
): number {
  const exponential = initialDelayMs * 2 ** Math.max(0, attempt - 1);
  const capped = Math.min(maxDelayMs, exponential);
  // full jitter: [capped/2, capped]
  return Math.round(capped / 2 + Math.random() * (capped / 2));
}

type RetryExchangeOptions = {
  maxAttempts?: number;
  initialDelayMs?: number;
  maxDelayMs?: number;
  shouldRetry?: (
    error: CombinedError | undefined,
    operation: Operation | undefined,
  ) => boolean;
};

/**
 * transient な read error を retry する urql exchange を生成する。
 * fetchExchange の直前に置くこと（retry した operation は forward 経由で再 fetch される）。
 */
export const createRetryExchange =
  (options: RetryExchangeOptions = {}): Exchange =>
  ({ forward }) =>
  (ops$) => {
    const maxAttempts = options.maxAttempts ?? DEFAULT_MAX_ATTEMPTS;
    const initialDelayMs = options.initialDelayMs ?? DEFAULT_INITIAL_DELAY_MS;
    const maxDelayMs = options.maxDelayMs ?? DEFAULT_MAX_DELAY_MS;
    const shouldRetry = options.shouldRetry ?? isRetryableError;

    const { source: retry$, next: dispatchRetry } = makeSubject<Operation>();

    const attemptsOf = (operation: Operation): number =>
      (operation.context as { retryCount?: number }).retryCount ?? 0;

    // 元の operations と retry した operations を同じ forward に流し、結果を共有する。
    const shared$ = pipe(merge([ops$, retry$]), forward, share);

    const willRetry = (result: OperationResult): boolean => {
      if (!result.error || !shouldRetry(result.error, result.operation)) {
        return false;
      }
      return attemptsOf(result.operation) + 1 < maxAttempts;
    };

    // 下流へ流す結果: 成功 / retry 非対象 / retry 上限到達（最後の error をそのまま返す）。
    const forwarded$ = pipe(
      shared$,
      filter((result) => !willRetry(result)),
    );

    // retry 対象: backoff 後に retry subject へ再投入。結果自体は下流に流さない。
    const retries$ = pipe(
      shared$,
      filter((result) => willRetry(result)),
      mergeMap((result) => {
        const nextAttempt = attemptsOf(result.operation) + 1;
        const retried = makeOperation(result.operation.kind, result.operation, {
          ...result.operation.context,
          retryCount: nextAttempt,
        });
        // retry の再投入は subject 経由で行い、この source 自体は結果を流さない。
        return pipe(
          fromValue(retried),
          delay(computeBackoffMs(nextAttempt, initialDelayMs, maxDelayMs)),
          onPush((operation) => dispatchRetry(operation)),
          filter(() => false),
        ) as unknown as Source<OperationResult>;
      }),
    );

    return merge([forwarded$, retries$]);
  };
